import React, { useState } from 'react';
import { Calendar } from '../../types/calendar';
import PaymentOptionsForm from './PaymentOptionsForm';
import { createStripePrice } from '../../services/stripeService';
import { supabase } from '../../lib/supabase';
import { toast } from '../../utils/toast';

interface PaymentOptionsSectionProps {
  calendar: Calendar;
  userId: string;
  onUpdate: () => Promise<void>;
}

export default function PaymentOptionsSection({ calendar, userId, onUpdate }: PaymentOptionsSectionProps) {
  const [saving, setSaving] = useState(false);
  const [paymentType, setPaymentType] = useState<'free' | 'subscription' | 'one_time'>(
    calendar.payment_type as 'free' | 'subscription' | 'one_time' || 'free'
  );
  const [subscriptionPriceCents, setSubscriptionPriceCents] = useState<number | null>(
    calendar.subscription_price_cents
  );
  const [eventPriceCents, setEventPriceCents] = useState<number | null>(
    calendar.event_price_cents
  );
  const [subscriptionLimit, setSubscriptionLimit] = useState<number | null>(
    calendar.subscription_limit
  );
  const [eventTicketLimit, setEventTicketLimit] = useState<number | null>(
    calendar.event_ticket_limit
  );

  const handleSave = async () => {
    if (saving) return;

    setSaving(true);
    let stripePriceId = calendar.stripe_price_id;

    try {
      // Always create a new Stripe price when subscription is enabled
      // This ensures the price matches the current Stripe key mode (test/live)
      if (paymentType === 'subscription' && subscriptionPriceCents) {
        try {
          const price = await createStripePrice({
            amount: subscriptionPriceCents,
            name: calendar.name,
            description: calendar.description || undefined
          });
          stripePriceId = price.id;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          console.error('Error creating Stripe price:', errorMessage);
          toast.error('Failed to create Stripe price. Please check your Stripe configuration.');
          setSaving(false);
          return;
        }
      }

      // Update calendar payment settings
      const { error: updateError } = await supabase
        .from('calendars')
        .update({
          payment_type: paymentType,
          subscription_price_cents: paymentType === 'subscription' ? subscriptionPriceCents : null,
          event_price_cents: paymentType === 'one_time' ? eventPriceCents : null,
          subscription_limit: paymentType === 'subscription' ? subscriptionLimit : null,
          event_ticket_limit: paymentType === 'one_time' ? eventTicketLimit : null,
          stripe_price_id: stripePriceId
        })
        .eq('id', calendar.id)
        .eq('user_id', userId);

      if (updateError) {
        throw new Error(`Failed to update calendar: ${updateError.message}`);
      }

      await onUpdate();
      toast.success('Payment settings updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('Error updating payment settings:', errorMessage);
      toast.error('Failed to update payment settings. Please try again later or contact support if the problem persists.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Payment Settings</h2>
      
      <PaymentOptionsForm
        paymentType={paymentType}
        subscriptionPriceCents={subscriptionPriceCents}
        eventPriceCents={eventPriceCents}
        subscriptionLimit={subscriptionLimit}
        eventTicketLimit={eventTicketLimit}
        onPaymentTypeChange={setPaymentType}
        onSubscriptionPriceChange={setSubscriptionPriceCents}
        onEventPriceChange={setEventPriceCents}
        onSubscriptionLimitChange={setSubscriptionLimit}
        onEventTicketLimitChange={setEventTicketLimit}
      />

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}